import { useState, type ReactNode } from 'react'
import { useT } from '../i18n'

/**
 * Схлопываемая легенда карты. В покое — маленькая закладка-таб; полное тело
 * всплывает при наведении курсора (десктоп) или тапе (тач) и прячется при уходе
 * курсора или повторном тапе.
 *
 * Показ/скрытие тела:
 * - десктоп (hover: hover) — чистым CSS по `:hover` контейнера (см. index.css);
 * - тач — состоянием `open`, переключаемым тапом по табу/телу.
 *
 * `mouseleave` сбрасывает `open` ТОЛЬКО на hover-устройствах — иначе синтетический
 * mouseleave после тапа на тач-экране закрыл бы легенду сразу же.
 *
 * Тело рендерится в потоке, поэтому контейнер, привязанный снизу (родитель ставит
 * `bottom-…`), растёт вверх, а курсор с таба попадает в нижнюю кромку тела без
 * разрыва ховера.
 */
export function CollapsibleLegend({ title, children }: { title: string; children: ReactNode }) {
  const t = useT()
  const [open, setOpen] = useState(false)

  const canHover = () =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches

  return (
    <div
      className="legend-pop pointer-events-auto"
      data-open={open ? 'true' : 'false'}
      onMouseLeave={() => {
        if (canHover()) setOpen(false)
      }}
    >
      <button
        type="button"
        className="legend-pop-tab flex items-center gap-1.5 rounded-lg border border-hull bg-abyss/85 px-2.5 py-1.5 text-[11px] font-semibold text-foam shadow-console backdrop-blur"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={t('map.legendAria', { title })}
      >
        <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 opacity-70" aria-hidden>
          {/* Шеврон вверх — тело всплывает над закладкой. */}
          <path
            d="M2 8l4-4 4 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {title}
      </button>
      <div className="legend-pop-body">{children}</div>
    </div>
  )
}
